import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import { formatNorwegianDate } from '../../utils/dateUtils'
import { useMaxDataDate } from '../../hooks/useMaxDataDate'
import { useHendelser, useHendelserForhandlere } from './hooks/useHendelser'
import { MIN_DATE } from './BruktbilfinansieringFilterBar'
import type { HendelseSource, HendelseType } from '../../types/bruktbilfinansiering'

interface HendelserTabProps {
  bankOrgNr: string
  bankName: string
}

const PAGE_SIZE = 50
const WINDOW_DAYS = 30

const _nb = new Intl.NumberFormat('nb-NO')

// Leasing is absent on purpose: a leasing company does not buy the car it
// leases out, so a leasing row says nothing about the dealer's activity.
const TYPE_LABEL_KEYS = {
  eie: 'hendelser.types.eie',
  kjopt: 'hendelser.types.kjopt',
  solgt: 'hendelser.types.solgt',
} as const satisfies Record<HendelseType, string>

const TYPE_STYLES: Record<HendelseType, string> = {
  eie: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
  kjopt: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  solgt: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
}

const SOURCE_LABEL_KEYS = {
  nybil: 'hendelser.sources.nybil',
  bruktbil: 'hendelser.sources.bruktbil',
} as const satisfies Record<HendelseSource, string>

function toISO(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function daysBefore(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() - n)
  return toISO(d)
}

/**
 * Hendelser: every car that moved through the bank's agent dealerships in the
 * selected window — new-car registrations in the dealer's own name, used-car
 * purchases and used-car sales.
 *
 * The tab owns its period rather than following the module's filter bar. That
 * bar defaults its end date to today - 15 days, the horizon at which Brreg
 * heftelse has settled, which is the right default for every tab that reports
 * financing. Here it silently cut the two most recent weeks of events off a
 * list that has nothing to do with loans. This period runs to the newest
 * transfer in the data instead.
 */
export default function HendelserTab({ bankOrgNr, bankName }: HendelserTabProps) {
  const { t } = useTranslation('bbf')
  const maxDataDate = useMaxDataDate()
  const latest = maxDataDate ?? toISO(new Date())

  const [startDate, setStartDate] = useState(() => {
    const from = daysBefore(latest, WINDOW_DAYS)
    return from < MIN_DATE ? MIN_DATE : from
  })
  const [endDate, setEndDate] = useState(latest)
  const [type, setType] = useState<HendelseType | ''>('')
  const [source, setSource] = useState<HendelseSource | ''>('')
  const [dealerOrgNr, setDealerOrgNr] = useState('')
  const [page, setPage] = useState(1)

  const dealers = useHendelserForhandlere(bankOrgNr)

  const rangeInvalid = startDate > endDate

  // Any filter change is a different result set; staying on page 7 of it would
  // show an empty table.
  const query = useMemo(() => ({
    startDate, endDate, type, source, dealerOrgNr, page, pageSize: PAGE_SIZE,
  }), [startDate, endDate, type, source, dealerOrgNr, page])

  function change<T>(set: (v: T) => void) {
    return (v: T) => { set(v); setPage(1) }
  }

  const { data, loading, error } = useHendelser(bankOrgNr, query)

  const summary = data?.summary
  const pagination = data?.pagination
  const rows = data?.rows ?? []

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
        <p className="mb-1">
          {t('hendelser.intro')}
        </p>
        <p className="text-xs text-blue-600 dark:text-blue-400">
          <span className="font-medium">{bankName}</span>
          {' · '}
          {t('hendelser.periodNote')}
        </p>
      </div>

      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="hendelse-forhandler" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('shared.dealer')}
            </label>
            <select
              id="hendelse-forhandler"
              value={dealerOrgNr}
              onChange={e => change(setDealerOrgNr)(e.target.value)}
              className="input-field text-sm min-w-[260px]"
            >
              <option value="">{t('hendelser.allDealers', { n: dealers.length })}</option>
              {dealers.map(d => (
                <option key={d.org_nr} value={d.org_nr}>
                  {d.org_navn} ({d.org_nr})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="hendelse-fra" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('shared.fromDate')}
            </label>
            <input
              id="hendelse-fra"
              type="date"
              value={startDate}
              min={MIN_DATE}
              max={endDate}
              onChange={e => change(setStartDate)(e.target.value)}
              className="input-field text-sm"
            />
          </div>
          <div>
            <label htmlFor="hendelse-til" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('shared.toDate')}
            </label>
            <input
              id="hendelse-til"
              type="date"
              value={endDate}
              min={startDate > MIN_DATE ? startDate : MIN_DATE}
              max={latest}
              onChange={e => change(setEndDate)(e.target.value)}
              className="input-field text-sm"
            />
          </div>
          <div>
            <label htmlFor="hendelse-type" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('hendelser.type')}
            </label>
            <select
              id="hendelse-type"
              value={type}
              onChange={e => change(setType)(e.target.value as HendelseType | '')}
              className="input-field text-sm min-w-[120px]"
            >
              <option value="">{t('shared.all')}</option>
              {(Object.keys(TYPE_LABEL_KEYS) as HendelseType[]).map(ty => (
                <option key={ty} value={ty}>{t(TYPE_LABEL_KEYS[ty])}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="hendelse-kilde" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('hendelser.source')}
            </label>
            <select
              id="hendelse-kilde"
              value={source}
              onChange={e => change(setSource)(e.target.value as HendelseSource | '')}
              className="input-field text-sm min-w-[120px]"
            >
              <option value="">{t('shared.all')}</option>
              {(Object.keys(SOURCE_LABEL_KEYS) as HendelseSource[]).map(s => (
                <option key={s} value={s}>{t(SOURCE_LABEL_KEYS[s])}</option>
              ))}
            </select>
          </div>
          {rangeInvalid && (
            <span className="text-xs text-red-600 dark:text-red-400 pb-2">
              {t('hendelser.rangeInvalid')}
            </span>
          )}
        </div>

        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm border-t border-gray-200 dark:border-gray-700 pt-3">
            <div>
              <div className="text-xs text-gray-500">{t('hendelser.events')}</div>
              <div className="text-lg font-bold">{_nb.format(summary.total)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">{t('hendelser.types.eie')}</div>
              <div className="text-lg font-bold">{_nb.format(summary.eie_count)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">{t('hendelser.types.kjopt')}</div>
              <div className="text-lg font-bold">{_nb.format(summary.kjopt_count)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">{t('hendelser.types.solgt')}</div>
              <div className="text-lg font-bold">{_nb.format(summary.solgt_count)}</div>
            </div>
          </div>
        )}
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="card p-6">
        {loading ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            {t('hendelser.empty')}
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('hendelser.eventsCount', { n: _nb.format(pagination?.total ?? 0) })}
            </p>

            <div className="overflow-x-auto">
              <table className="data-table data-table-compact w-full">
                <thead>
                  <tr>
                    <th className="text-left whitespace-nowrap">{t('shared.date')}</th>
                    <th className="text-left whitespace-nowrap">{t('hendelser.type')}</th>
                    <th className="text-left whitespace-nowrap">{t('hendelser.source')}</th>
                    <th className="text-left whitespace-nowrap">{t('hendelser.cols.regNo')}</th>
                    <th className="text-left whitespace-nowrap">{t('shared.brand')}</th>
                    <th className="text-left">{t('shared.model')}</th>
                    <th className="text-left whitespace-nowrap">{t('hendelser.cols.firstReg')}</th>
                    <th className="text-left whitespace-nowrap">{t('hendelser.cols.orgNr')}</th>
                    <th className="text-left">{t('shared.dealer')}</th>
                    <th className="text-left whitespace-nowrap">{t('hendelser.cols.postnr')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr
                      key={`${row.type}-${row.kjennemerke}-${row.hendelse_dato}-${idx}`}
                      className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors`}
                    >
                      <td className="text-sm whitespace-nowrap">{formatNorwegianDate(row.hendelse_dato)}</td>
                      <td>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs ${TYPE_STYLES[row.type]}`}>
                          {t(TYPE_LABEL_KEYS[row.type])}
                        </span>
                      </td>
                      <td className="text-sm">{t(SOURCE_LABEL_KEYS[row.source])}</td>
                      <td className="font-mono text-base font-bold tracking-wide whitespace-nowrap">
                        {row.kjennemerke ?? '—'}
                      </td>
                      <td className="text-sm">{row.merke ?? '—'}</td>
                      <td className="text-sm">{row.modell ?? '—'}</td>
                      <td className="text-sm whitespace-nowrap">
                        {row.forstegang_reg ? formatNorwegianDate(row.forstegang_reg) : '—'}
                      </td>
                      <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{row.org_nr ?? '—'}</td>
                      <td className="text-sm">{row.org_navn ?? '—'}</td>
                      <td className="font-mono text-xs">{row.postnummer ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4 text-sm">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('hendelser.prev')}
                </button>
                <span className="text-gray-600 dark:text-gray-400">
                  {t('hendelser.page', { page: _nb.format(pagination.page), total: _nb.format(pagination.total_pages) })}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= pagination.total_pages}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('hendelser.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
